// src/pages/UtforBetaling.jsx
// "Utfør betaling": ansatte taster referansenummeret på servicelappen eller
// skanner QR-koden (kamera, eller en håndskanner som "taster" inn innholdet
// og trykker Enter). Går videre til /betal/<ref>.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";

const SCANNER_ID = "utfor-betaling-scanner";

// Godtar "12345", "#12345" og hele QR-adressen (…/betal/12345).
export function parseServiceRef(text) {
  const s = String(text || "").trim();
  const fraUrl = s.match(/\/betal\/(\d{5})(?:[/?#]|$)/);
  if (fraUrl) return fraUrl[1];
  const ren = s.match(/^#?\s*(\d{5})$/);
  return ren ? ren[1] : null;
}

export default function UtforBetaling() {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [feil, setFeil] = useState("");
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    document.title = "Utfør betaling – Røyken Sykkelservice";
    inputRef.current?.focus();
  }, []);

  function gaTil(text) {
    const ref = parseServiceRef(text);
    if (!ref) {
      setFeil("Fant ikke et gyldig referansenummer. Det er fem sifre, f.eks. 04213.");
      return;
    }
    setFeil("");
    navigate(`/betal/${ref}`);
  }

  async function stoppSkanning() {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try { await scanner.stop(); } catch { /* allerede stoppet */ }
    }
    setScanning(false);
  }

  useEffect(() => {
    if (!scanning) return;
    const scanner = new Html5Qrcode(SCANNER_ID);
    scannerRef.current = scanner;
    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        async (decoded) => {
          await stoppSkanning();
          gaTil(decoded);
        },
        () => {}
      )
      .catch((err) => {
        setFeil("Kamera ikke tilgjengelig: " + (err?.message || err));
        scannerRef.current = null;
        setScanning(false);
      });
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  return (
    <div className="max-w-lg mx-auto">
      <h1 className="text-2xl font-bold mb-1">Utfør betaling</h1>
      <p className="text-gray-700 mb-4">
        Skann QR-koden på servicelappen, eller skriv inn referansenummeret.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          gaTil(input);
        }}
        className="flex flex-wrap items-end gap-2"
      >
        <div className="flex-1 min-w-[12rem]">
          <label htmlFor="betal-ref" className="block text-sm font-medium text-gray-800 mb-1">
            Referansenummer
          </label>
          <input
            id="betal-ref"
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            inputMode="numeric"
            autoComplete="off"
            placeholder="f.eks. 04213"
            aria-invalid={!!feil}
            aria-describedby={feil ? "betal-ref-feil" : undefined}
            className="w-full border border-gray-400 rounded px-3 py-2 text-lg font-mono focus:outline-none focus:ring-2 focus:ring-emerald-600"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 rounded bg-emerald-700 text-white font-semibold hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-700"
        >
          Finn service
        </button>
      </form>

      {feil && (
        <p id="betal-ref-feil" role="alert" className="mt-2 text-sm text-red-700">
          {feil}
        </p>
      )}

      <div className="mt-6">
        {scanning ? (
          <button
            type="button"
            onClick={stoppSkanning}
            className="px-4 py-2 rounded border border-gray-500 text-gray-800 hover:bg-gray-100"
          >
            Stopp kamera
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              setFeil("");
              setScanning(true);
            }}
            className="px-4 py-2 rounded border border-emerald-700 text-emerald-800 font-semibold hover:bg-emerald-50"
          >
            📷 Skann QR-kode med kamera
          </button>
        )}
        <div id={SCANNER_ID} className={scanning ? "mt-3 max-w-sm" : "hidden"} />
      </div>
    </div>
  );
}
